/**
 * ConstructorBOPISHelper.js
 * Handles all Constructor-specific BOPIS/Same Day Pickup logic.
 * Gated by isConstructorSearchEnabled — when false, old Solr behavior applies.
 */

/**
 * Checks if "same day pickup" option exists in Constructor's store_availability facet.
 * Used to determine if the BOPIS toggle should be displayed.
 */
export const hasConstructorSameDayPickup = (facets) => {
    if (!Array.isArray(facets)) return false;
    const storeAvailFacet = facets.find((f) => f && f.name === 'store_availability');
    if (!storeAvailFacet || !Array.isArray(storeAvailFacet.options)) return false;
    return storeAvailFacet.options.some(
        (opt) =>
            opt &&
            (opt.value === 'both' ||
                opt.value === 'same day pickup' ||
                opt.display_name === 'same day pickup')
    );
};

/**
 * Forces store_availability facet option to "selected" when storeIds is active.
 * Constructor may not return status: "selected" even when filtering by store_availability.
 */
export const forceStoreAvailabilitySelected = (facet, storeIds) => {
    if (!storeIds || facet.name !== 'store_availability' || !Array.isArray(facet.options))
        return facet;
    return {
        ...facet,
        options: facet.options.map((opt) =>
            opt && (opt.value === 'both' || opt.display_name === 'same day pickup')
                ? { ...opt, status: 'selected' }
                : opt
        ),
    };
};

/**
 * Appends storeIds and store_availability params to a facet apiUrl.
 * Ensures store selection is retained when navigating between facets.
 * Strips any Constructor-format params that may have leaked in.
 */
export const appendStoreParamsToUrl = (url, storeIds) => {
    if (!url || !storeIds) return url;
    let cleanUrl = url.replace(/&store_availability=[^&]*/g, '').replace(/&store_ids=[^&]*/g, '');
    if (!cleanUrl.includes('storeIds'))
        cleanUrl += `&storeIds=${storeIds}&store_availability=same+day+pickup`;
    return cleanUrl;
};

/**
 * Maps storeIds (JCPenney URL format) → filters[store_ids] (Constructor API format).
 * storeIds in URL is dash-separated: "1591-2051" → ['1591', '2051']
 */
export const mapStoreIdsToConstructorFilter = (storeIds) => {
    if (!storeIds || typeof storeIds !== 'string') return null;
    const values = storeIds.split('-').filter(Boolean);
    return values.length > 0 ? values : null;
};

/**
 * Maps store_availability URL param → Constructor filter value.
 * URL has "same+day+pickup" or "same day pickup" → Constructor needs "both" (or "same day pickup" if index updated).
 */
export const mapStoreAvailabilityToConstructorFilter = (storeAvailability) => {
    if (!storeAvailability) return null;
    const value = storeAvailability.replace(/\+/g, ' ');
    if (value.toLowerCase() === 'same day pickup') return 'both';
    return null;
};

/**
 * Checks if a facet should be excluded from selectedFacets chips.
 * store_availability and store_ids should not show as removable filter chips.
 */
export const isStoreFacet = (facetName) =>
    facetName === 'store_availability' || facetName === 'store_ids';

/**
 * Checks if a facet should be hidden from the filter panel list.
 * store_ids should not appear as a filterable facet.
 */
export const isHiddenStoreFacet = (facetName) => facetName === 'store_ids';

export default {
    hasConstructorSameDayPickup,
    forceStoreAvailabilitySelected,
    appendStoreParamsToUrl,
    mapStoreIdsToConstructorFilter,
    mapStoreAvailabilityToConstructorFilter,
    isStoreFacet,
    isHiddenStoreFacet,
};
